import { useState, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { radioStations, type RadioStation } from "@/mocks/radios";
import { useAudioPlayer } from "@/context/AudioContext";
import Swal from "sweetalert2";

/* ── Filter definitions ── */
const SEARCH_FILTERS = [
  { key: "Musica", label: "Música", icon: "ri-music-line" },
  { key: "Noticias", label: "Noticias", icon: "ri-newspaper-line" },
  { key: "Cristiana", label: "Cristiana", icon: "ri-cross-line" },
  { key: "Deportes", label: "Deportes", icon: "ri-football-line" },
  { key: "Regional", label: "Regional", icon: "ri-earth-line" },
  { key: "TalkShow", label: "Talk Show", icon: "ri-mic-line" },
] as const;

type FilterKey = (typeof SEARCH_FILTERS)[number]["key"];

/* Maps each filter to matching category names */
const FILTER_CATEGORY_MAP: Record<FilterKey, string[]> = {
  Musica: ["Pop", "Rock", "Jazz", "Electrónica", "Salsa", "Clásica", "Reggae", "Hip Hop", "Country", "Infantil"],
  Noticias: ["Noticias"],
  Cristiana: ["Cristiana"],
  Deportes: ["Deportes"],
  Regional: ["Regional Mexicana", "Comunitaria", "Internacional"],
  TalkShow: [], // no hay talk shows en los mocks, mostrará empty state
};

const INITIAL_FAVORITES = new Set([1, 5, 7, 16, 19]);
const INITIAL_FOLLOWING = new Set([1, 2, 7, 12, 14, 16]);

export default function AppSearchPage() {
  const navigate = useNavigate();
  const { play, isPlaying, currentStation, pause } = useAudioPlayer();
  const [query, setQuery] = useState("");
  const [activeFilter, setActiveFilter] = useState<FilterKey | null>(null);
  const [favorites, setFavorites] = useState<Set<number>>(() => {
    try {
      const raw = localStorage.getItem("rfw_app_favorites");
      return raw ? new Set<number>(JSON.parse(raw)) : new Set(INITIAL_FAVORITES);
    } catch {
      return new Set(INITIAL_FAVORITES);
    }
  });
  const [following, setFollowing] = useState<Set<number>>(() => {
    try {
      const raw = localStorage.getItem("rfw_app_following");
      return raw ? new Set<number>(JSON.parse(raw)) : new Set(INITIAL_FOLLOWING);
    } catch {
      return new Set(INITIAL_FOLLOWING);
    }
  });

  const persistFavorites = (next: Set<number>) => {
    setFavorites(next);
    localStorage.setItem("rfw_app_favorites", JSON.stringify([...next]));
  };

  const persistFollowing = (next: Set<number>) => {
    setFollowing(next);
    localStorage.setItem("rfw_app_following", JSON.stringify([...next]));
  };

  /* ── Search + filter logic ── */
  const results = useMemo(() => {
    let filtered = radioStations;

    // text search
    if (query.trim()) {
      const q = query.toLowerCase();
      filtered = filtered.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          s.frequency.includes(q) ||
          s.city.toLowerCase().includes(q) ||
          s.state.toLowerCase().includes(q) ||
          s.category.toLowerCase().includes(q) ||
          s.hostName.toLowerCase().includes(q),
      );
    }

    // active filter chip
    if (activeFilter) {
      const cats = FILTER_CATEGORY_MAP[activeFilter];
      if (cats.length > 0) {
        filtered = filtered.filter((s) => cats.includes(s.category));
      } else {
        // TalkShow: no match in current data
        filtered = [];
      }
    }

    return filtered;
  }, [query, activeFilter]);

  const hasAnyFilter = query.trim() !== "" || activeFilter !== null;

  /* ── Handlers ── */
  const handlePlay = useCallback(
    (e: React.MouseEvent, station: RadioStation) => {
      e.stopPropagation();
      const isThis = isPlaying && currentStation?.id === station.id;
      if (isThis) {
        pause();
      } else {
        play(station);
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: `Reproduciendo ${station.name}`,
          showConfirmButton: false,
          timer: 1800,
          timerProgressBar: true,
        });
      }
    },
    [isPlaying, currentStation, play, pause],
  );

  const handleEnter = useCallback(
    (e: React.MouseEvent, station: RadioStation) => {
      e.stopPropagation();
      navigate(`/radio/${station.frequency.replace(".", "-")}`);
    },
    [navigate],
  );

  const handleFollow = useCallback((e: React.MouseEvent, station: RadioStation) => {
    e.stopPropagation();
    setFollowing((prev) => {
      const next = new Set(prev);
      if (next.has(station.id)) {
        next.delete(station.id);
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "info",
          title: `Dejaste de seguir a "${station.name}"`,
          showConfirmButton: false,
          timer: 1800,
          timerProgressBar: true,
        });
      } else {
        next.add(station.id);
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: `Ahora seguís a "${station.name}"`,
          showConfirmButton: false,
          timer: 1800,
          timerProgressBar: true,
        });
      }
      persistFollowing(next);
      return next;
    });
  }, []);

  const handleFavorite = useCallback((e: React.MouseEvent, station: RadioStation) => {
    e.stopPropagation();
    setFavorites((prev) => {
      const next = new Set(prev);
      if (next.has(station.id)) {
        next.delete(station.id);
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "info",
          title: `"${station.name}" eliminada de favoritas`,
          showConfirmButton: false,
          timer: 1800,
          timerProgressBar: true,
        });
      } else {
        next.add(station.id);
        Swal.fire({
          toast: true,
          position: "top-end",
          icon: "success",
          title: `"${station.name}" agregada a favoritas`,
          showConfirmButton: false,
          timer: 1800,
          timerProgressBar: true,
        });
      }
      persistFavorites(next);
      return next;
    });
  }, []);

  const handleClearAll = useCallback(() => {
    setQuery("");
    setActiveFilter(null);
  }, []);

  const toggleFilter = (key: FilterKey) => {
    setActiveFilter((prev) => (prev === key ? null : key));
  };

  return (
    <div className="flex flex-col min-h-screen bg-background-50">
      {/* Header */}
      <header className="sticky top-0 z-30 bg-background-50/95 backdrop-blur-sm border-b border-background-200 safe-top">
        <div className="max-w-6xl mx-auto px-4 md:px-6 py-3 flex items-center gap-3">
          <button
            onClick={() => navigate("/app")}
            className="w-9 h-9 rounded-xl bg-background-100 hover:bg-background-200 text-foreground-600 flex items-center justify-center cursor-pointer shrink-0 transition-all"
          >
            <i className="ri-arrow-left-line"></i>
          </button>
          <div className="flex items-center gap-2 flex-1">
            <div className="w-9 h-9 rounded-xl bg-background-100 flex items-center justify-center">
              <i className="ri-search-line text-foreground-600 text-lg"></i>
            </div>
            <div>
              <h1 className="text-foreground-950 font-bold text-lg">Buscar</h1>
              <p className="text-foreground-400 text-[11px]">
                {radioStations.length} radios · nombre, frecuencia, ciudad, categoría o locutor
              </p>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="flex-1 max-w-6xl mx-auto w-full px-4 md:px-6 pb-20">
        {/* Sticky search bar */}
        <div className="sticky top-[57px] z-20 bg-background-50/95 backdrop-blur-sm pt-4 pb-2">
          {/* Search input */}
          <div className="relative">
            <i className="ri-search-line absolute left-3.5 top-1/2 -translate-y-1/2 text-foreground-400 text-base"></i>
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Buscar por nombre, frecuencia, ciudad, categoría o locutor..."
              className="w-full pl-10 pr-12 py-3 text-sm bg-background-50 border border-background-200 rounded-xl text-foreground-900 placeholder-foreground-400 outline-none focus:border-primary-400 focus:ring-2 focus:ring-primary-200/50 transition-all"
            />
            {query && (
              <button
                onClick={() => setQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center rounded-full bg-background-200 hover:bg-background-300 cursor-pointer transition-all"
              >
                <i className="ri-close-line text-foreground-500 text-xs"></i>
              </button>
            )}
          </div>

          {/* Filter chips */}
          <div className="flex items-center gap-1.5 mt-2 overflow-x-auto scrollbar-none pb-1">
            {SEARCH_FILTERS.map((f) => (
              <button
                key={f.key}
                onClick={() => toggleFilter(f.key)}
                className={`inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full text-[11px] font-semibold whitespace-nowrap transition-all cursor-pointer ${
                  activeFilter === f.key
                    ? "bg-secondary-500 text-background-50"
                    : "bg-background-100 text-foreground-500 hover:bg-background-200"
                }`}
              >
                <i className={`${f.icon} text-[10px]`}></i>
                {f.label}
              </button>
            ))}
          </div>

          {/* Results count + clear */}
          {hasAnyFilter && (
            <div className="flex items-center justify-between mt-2">
              <p className="text-foreground-400 text-[11px]">
                {results.length} {results.length === 1 ? "resultado" : "resultados"}
              </p>
              <button
                onClick={handleClearAll}
                className="flex items-center gap-1 text-foreground-500 hover:text-foreground-700 text-[11px] font-medium cursor-pointer transition-all"
              >
                <i className="ri-close-circle-line text-xs"></i>
                Limpiar filtros
              </button>
            </div>
          )}
        </div>

        {/* Results */}
        <div className="mt-1 space-y-2">
          {results.length === 0 && hasAnyFilter ? (
            <EmptySearchState
              onExplore={() => navigate("/explorar")}
              onTrending={() => navigate("/app/trending")}
              onClear={handleClearAll}
            />
          ) : results.length === 0 ? (
            /* No filters active — show all */
            <>
              <p className="text-foreground-400 text-[11px]">
                {radioStations.length} radios disponibles
              </p>
              {radioStations.map((station) => (
                <SearchResultCard
                  key={station.id}
                  station={station}
                  isPlaying={isPlaying}
                  currentStation={currentStation}
                  isFav={favorites.has(station.id)}
                  isFollowing={following.has(station.id)}
                  onPlay={handlePlay}
                  onEnter={handleEnter}
                  onFollow={handleFollow}
                  onFavorite={handleFavorite}
                />
              ))}
            </>
          ) : (
            <>
              {results.map((station) => (
                <SearchResultCard
                  key={station.id}
                  station={station}
                  isPlaying={isPlaying}
                  currentStation={currentStation}
                  isFav={favorites.has(station.id)}
                  isFollowing={following.has(station.id)}
                  onPlay={handlePlay}
                  onEnter={handleEnter}
                  onFollow={handleFollow}
                  onFavorite={handleFavorite}
                />
              ))}
            </>
          )}
        </div>
      </main>
    </div>
  );
}

/* ── Empty State ── */
function EmptySearchState({
  onExplore,
  onTrending,
  onClear,
}: {
  onExplore: () => void;
  onTrending: () => void;
  onClear: () => void;
}) {
  return (
    <div className="flex flex-col items-center justify-center py-16 text-center">
      <div className="w-20 h-20 rounded-full bg-background-100 flex items-center justify-center mb-5">
        <i className="ri-search-line text-foreground-300 text-3xl"></i>
      </div>
      <h3 className="text-foreground-700 font-semibold text-base">
        No encontramos radios con esa búsqueda
      </h3>
      <p className="text-foreground-400 text-sm mt-1 max-w-xs">
        Probá con otros términos o explorá otras opciones.
      </p>
      <div className="flex items-center gap-3 mt-5">
        <button
          onClick={onTrending}
          className="px-4 py-2.5 bg-primary-500 hover:bg-primary-600 text-background-50 text-sm font-semibold rounded-xl cursor-pointer whitespace-nowrap transition-all flex items-center gap-2"
        >
          <i className="ri-fire-line"></i>
          Ver tendencias
        </button>
        <button
          onClick={onExplore}
          className="px-4 py-2.5 bg-background-100 hover:bg-background-200 text-foreground-600 text-sm font-semibold rounded-xl cursor-pointer whitespace-nowrap transition-all flex items-center gap-2"
        >
          <i className="ri-compass-3-line"></i>
          Explorar radios
        </button>
      </div>
    </div>
  );
}

/* ── Result Card ── */
interface SearchResultCardProps {
  station: RadioStation;
  isPlaying: boolean;
  currentStation: RadioStation | null;
  isFav: boolean;
  isFollowing: boolean;
  onPlay: (e: React.MouseEvent, station: RadioStation) => void;
  onEnter: (e: React.MouseEvent, station: RadioStation) => void;
  onFollow: (e: React.MouseEvent, station: RadioStation) => void;
  onFavorite: (e: React.MouseEvent, station: RadioStation) => void;
}

function SearchResultCard({
  station,
  isPlaying,
  currentStation,
  isFav,
  isFollowing,
  onPlay,
  onEnter,
  onFollow,
  onFavorite,
}: SearchResultCardProps) {
  const isThisPlaying = isPlaying && currentStation?.id === station.id;

  return (
    <div className="group bg-background-50 border border-background-200 hover:border-foreground-300 rounded-2xl p-4 transition-all duration-200">
      <div className="flex items-start gap-3">
        {/* Logo */}
        <div className="w-14 h-14 rounded-xl overflow-hidden bg-background-100 border border-background-200 shrink-0">
          <img
            src={station.logoUrl}
            alt={station.name}
            className="w-full h-full object-cover"
            loading="lazy"
          />
        </div>

        {/* Info */}
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <h3 className="text-foreground-950 font-bold text-sm truncate">{station.name}</h3>
              <p className="text-foreground-500 text-xs mt-0.5 truncate">
                {station.hostName}
              </p>
            </div>
            {station.isLive && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 text-red-600 border border-red-200 text-[10px] font-bold shrink-0">
                <span className="w-1.5 h-1.5 rounded-full bg-red-500 animate-pulse"></span>
                LIVE
              </span>
            )}
          </div>

          {/* Meta */}
          <div className="flex items-center gap-2 mt-1 flex-wrap">
            <span className="text-foreground-600 text-[11px] font-semibold tabular-nums">
              {station.frequency}
            </span>
            <span className="w-1 h-1 rounded-full bg-foreground-300 shrink-0"></span>
            <span className="text-foreground-500 text-[11px]">
              {station.city}, {station.state}
            </span>
          </div>

          {/* Tags */}
          <div className="flex items-center gap-1.5 mt-1.5 flex-wrap">
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-secondary-50 border border-secondary-200 text-secondary-700 text-[10px] font-medium">
              {station.category}
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-background-100 border border-background-200 text-foreground-500 text-[10px] font-medium">
              {station.language}
            </span>
            {station.isPremium && (
              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-amber-50 border border-amber-200 text-amber-600 text-[10px] font-medium">
                <i className="ri-vip-diamond-line text-[9px]"></i>
                Premium
              </span>
            )}
          </div>

          {/* Stats */}
          <div className="flex items-center gap-3 mt-1.5 text-[10px]">
            <div className="flex items-center gap-1 text-foreground-500">
              <i className="ri-headphone-line"></i>
              <span className="font-semibold">
                {station.listeners >= 1000 ? `${(station.listeners / 1000).toFixed(1)}K` : station.listeners}
              </span>
            </div>
            <div className="flex items-center gap-1 text-foreground-500">
              <i className="ri-star-fill text-amber-500"></i>
              <span className="font-semibold">{station.radioScore.toFixed(1)}</span>
            </div>
            <div className="flex items-center gap-1 text-foreground-500">
              <i className="ri-chat-1-line"></i>
              <span className="font-semibold">{station.cabinaMessages}</span>
            </div>
          </div>
        </div>

        {/* Action buttons */}
        <div className="flex flex-col items-center gap-1 shrink-0">
          {/* Play / Pause */}
          <button
            onClick={(e) => onPlay(e, station)}
            className={`w-10 h-10 rounded-xl flex items-center justify-center cursor-pointer transition-all ${
              isThisPlaying
                ? "bg-accent-500 text-background-50"
                : "bg-primary-500 text-background-50 hover:bg-primary-600"
            }`}
            title={isThisPlaying ? "Pausar" : "Reproducir"}
          >
            <i className={`text-base ${isThisPlaying ? "ri-pause-fill" : "ri-play-fill"}`}></i>
          </button>

          {/* Enter radio */}
          <button
            onClick={(e) => onEnter(e, station)}
            className="w-10 h-10 rounded-xl flex items-center justify-center cursor-pointer bg-background-100 text-foreground-500 hover:bg-background-200 hover:text-foreground-700 transition-all"
            title="Entrar a la radio"
          >
            <i className="ri-arrow-right-line text-sm"></i>
          </button>

          {/* Follow */}
          <button
            onClick={(e) => onFollow(e, station)}
            className={`w-10 h-10 rounded-xl flex items-center justify-center cursor-pointer transition-all ${
              isFollowing
                ? "bg-accent-100 text-accent-500"
                : "bg-background-100 text-foreground-400 hover:bg-background-200 hover:text-accent-500"
            }`}
            title={isFollowing ? "Dejar de seguir" : "Seguir radio"}
          >
            <i className={`text-sm ${isFollowing ? "ri-notification-3-fill" : "ri-notification-3-line"}`}></i>
          </button>

          {/* Favorite */}
          <button
            onClick={(e) => onFavorite(e, station)}
            className={`w-10 h-10 rounded-xl flex items-center justify-center cursor-pointer transition-all ${
              isFav
                ? "bg-red-50 text-red-500"
                : "bg-background-100 text-foreground-400 hover:bg-background-200 hover:text-red-400"
            }`}
            title={isFav ? "Quitar de favoritas" : "Agregar a favoritas"}
          >
            <i className={`text-sm ${isFav ? "ri-heart-fill" : "ri-heart-line"}`}></i>
          </button>
        </div>
      </div>
    </div>
  );
}